import { FilterSetType, AccessListUser, Gender, UserConfig } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { playerPositionsOptionsAtom } from '@/atoms/queries/player/playerPositions';
import { CONFIG_FILTER_KEY_SET } from '@/consts/filters/configFilterKeys';
import { PHYSICAL_RADAR_TEMPLATE_CONFIG_IDS } from '@/consts/radar';
import {
  DEFAULT_GAME_DASHBOARD_ID,
  DEFAULT_PLAYER_DASHBOARD_ID,
  DEFAULT_TEAM_DASHBOARD_ID,
} from '@/consts/userConfigs';
import { Nullable } from '@/types/generic';
import {
  UserConfigCamelCase,
  UserConfigDashboardTemplateDefinition,
  UserConfigFilterDefinition,
  UserConfigOnboardingDefinition,
  UserConfigRadarTemplate,
  UserConfigSettings,
  UserConfigTableColumnsDefinition,
  UserConfigType,
  UserConfigWidgetDefinition,
} from '@/types/userConfigs';
import { getWidgetsByDashboardId } from '@/utils/atoms/userConfigs';
import { setUserLocaleCookie, tryParseLocaleCode } from '@/utils/i18n';
import { objSnakeToCamel } from '@/utils/queries';
import { isGkPosition } from '@/utils/radar';
import { canUserSeeDevTeamFeatures } from '@/utils/user';
import { filterRadarTemplates, formatRadarTemplates } from '@/utils/userConfigs';
import { userConfigIdAtom } from '../userConfigs';
import { defaultTeamInfoAtom } from './teams';
import { rawUserDetailsAtom } from './user';

export const rawUserConfigPermissionsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const userConfigId = get(userConfigIdAtom);
  const queryKey = ['userConfigPermissions', userConfigId] as const;

  const queryFn = async ({ queryKey: [_, userConfigId] }: { queryKey: typeof queryKey }) => {
    if (!userConfigId) {
      return [];
    }
    return (await fetch(`/user/config/${userConfigId}/permissions`)) as AccessListUser[];
  };

  return { queryKey, queryFn };
});

export const userConfigPermissionsAtom = atom(async get => (await get(rawUserConfigPermissionsAtom)).data);

export const USER_CONFIGS_QUERY_KEY = ['userConfigs'] as const;

function userConfigsQueryFn(fetch: (url: string) => Promise<unknown>) {
  return async () => {
    // filter out deleted configs
    const results = (await fetch(`/user/configs?null[deleted_at]=true`)) as UserConfig[];

    // Set locale cookie based on the user's settings config, if it exists
    // - This is really an API layer concern but since the parachute-soccer-api is not hosted on a
    //   .thorhudl.com / .hudl.com subdomain we cannot set that cookie via the response headers
    // - As such the cookie is set here to remain as close to that API layer as possible
    const userLocaleConfig = results.find(config => config.type === UserConfigType.SETTINGS)?.definition?.locale;
    setUserLocaleCookie(userLocaleConfig);

    return results.map(objSnakeToCamel) as UserConfigCamelCase[];
  };
}

export const userConfigsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  return {
    queryKey: USER_CONFIGS_QUERY_KEY,
    queryFn: userConfigsQueryFn(fetch),
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  };
});

// singular config
const configByTypeAtom = <T>(type: string) =>
  atom(async get => {
    const { data } = await get(userConfigsAtom);
    return (data.find(config => config.type === type) || null) as T | null;
  });

// multiple configs
const configsByTypeAtom = <T>(type: string | string[]) =>
  atom(async get => {
    const { data } = await get(userConfigsAtom);
    if (Array.isArray(type)) {
      return data.filter(config => type.includes(config.type as UserConfigType)) as T;
    }
    return data.filter(config => (config.type as UserConfigType) === type) as T;
  });

// Drops filter keys the app no longer understands (e.g. the legacy `playTypes` key from configs
// saved before the EPT-33537 playTypes -> playPatterns rename) so hydration/counting never hits a
// key with no matching hydrate callback.
const stripUnknownFilterKeys = (config: UserConfigFilterDefinition): UserConfigFilterDefinition => {
  const filters = config.definition?.filters;
  if (!filters) return config;

  const knownEntries = Object.entries(filters).filter(([key]) => CONFIG_FILTER_KEY_SET.has(key));
  if (knownEntries.length === Object.keys(filters).length) return config;

  return { ...config, definition: { ...config.definition, filters: Object.fromEntries(knownEntries) } };
};

// multiple configs
export const dashboardTemplatesAtom = configsByTypeAtom<UserConfigDashboardTemplateDefinition[]>('dashboardTemplate');
export const dashboardWidgetsAtom = configsByTypeAtom<UserConfigWidgetDefinition[]>('widget');
const rawFilterSetsAtom = configsByTypeAtom<UserConfigFilterDefinition[]>(Object.values(FilterSetType));
export const filterSetsAtom = atom(async get => (await get(rawFilterSetsAtom)).map(stripUnknownFilterKeys));
export const customTableColumnsAtom = configsByTypeAtom<UserConfigTableColumnsDefinition[]>(
  UserConfigType.TABLE_COLUMNS,
);
export const radarTemplatesAtom = configsByTypeAtom<UserConfigRadarTemplate[]>(UserConfigType.RADAR_TEMPLATE);

// singular config
export const settingsConfigAtom = configByTypeAtom<UserConfigSettings>(UserConfigType.SETTINGS);
export const onboardingConfigAtom = configByTypeAtom<UserConfigOnboardingDefinition>(UserConfigType.ONBOARDING);
onboardingConfigAtom.debugLabel = 'onboardingConfigAtom';

export const unwrappedOnboardingConfigAtom = unwrap(onboardingConfigAtom);

/* I've used unwrap here as some places that use this configs atom need it synchronously or they behave
slightly weirdly. If you use the async atom when loading a filter set it jumps a bit while it's loading and 
this change to use the synchronous atom resolves this. */
export const unwrappedFilterSetsAtom = unwrap(filterSetsAtom, prev => prev || null);

export const widgetGenderAtom = atom<Nullable<Gender>>(null);

export const genderAtom = atom(async get => {
  const widgetGender = get(widgetGenderAtom);

  // in the context of a widget use the widget version of gender atom
  if (widgetGender) {
    return widgetGender;
  }

  // use preferred gender from settings config if available
  const settingsConfig = await get(settingsConfigAtom);
  if (settingsConfig?.definition?.gender) {
    return settingsConfig.definition.gender;
  }

  // otherwise, use the user's default team gender
  const defaultTeamInfo = await get(defaultTeamInfoAtom);
  if (defaultTeamInfo) {
    return defaultTeamInfo.gender;
  }

  return undefined;
});

export const unwrappedGenderAtom = unwrap(genderAtom, prev => prev || undefined);

// Returns the user's explicitly-chosen locale. Consumers handle the fallback chain
//  - reading i18n.language here causes a race condition with useI18nBridge
export const userLocaleAtom = atom(async get => {
  const settingsConfig = await get(settingsConfigAtom);
  return tryParseLocaleCode(settingsConfig?.definition?.locale);
});

const playerDashboardWidgetsAtom = atom(async get => getWidgetsByDashboardId(get, DEFAULT_PLAYER_DASHBOARD_ID));
export const unwrappedPlayerDashboardWidgetsAtom = unwrap(playerDashboardWidgetsAtom, prev => prev);

const teamDashboardWidgetsAtom = atom(async get => getWidgetsByDashboardId(get, DEFAULT_TEAM_DASHBOARD_ID));
export const unwrappedTeamDashboardWidgetsAtom = unwrap(teamDashboardWidgetsAtom, prev => prev);

const gameDashboardWidgetsAtom = atom(async get => getWidgetsByDashboardId(get, DEFAULT_GAME_DASHBOARD_ID));
export const unwrappedGameDashboardWidgetsAtom = unwrap(gameDashboardWidgetsAtom, prev => prev);

export const canSeePhysicalRadarAtom = atom(async get => {
  try {
    const { data: userDetails } = await get(rawUserDetailsAtom);
    return canUserSeeDevTeamFeatures(userDetails);
  } catch {
    return false;
  }
});

export const playerRadarTemplateConfigsAtom = atom(async get => {
  const [radarTemplates, gender, playerPositions, canSeePhysicalRadar] = await Promise.all([
    get(radarTemplatesAtom),
    get(genderAtom),
    get(playerPositionsOptionsAtom),
    get(canSeePhysicalRadarAtom),
  ]);

  const playerTemplateConfigs = filterRadarTemplates(radarTemplates, 'player', gender);

  const mostPlayedPosition = playerPositions[0]?.value;
  if (!canSeePhysicalRadar || isGkPosition(mostPlayedPosition)) {
    return playerTemplateConfigs.filter(config => !PHYSICAL_RADAR_TEMPLATE_CONFIG_IDS.has(config.configId));
  }

  return playerTemplateConfigs;
});

export const playerRadarTemplatesAtom = atom(async get => {
  const radarTemplateConfigs = await get(playerRadarTemplateConfigsAtom);
  const formattedPlayerRadarTemplates = formatRadarTemplates(radarTemplateConfigs);

  return formattedPlayerRadarTemplates;
});

export const unwrappedPlayerRadarTemplatesAtom = unwrap(playerRadarTemplatesAtom, prev => prev || []);

export const teamRadarTemplateConfigsAtom = atom(async get => {
  const radarTemplates = await get(radarTemplatesAtom);
  const gender = await get(genderAtom);

  // get all team radar templates
  // we filter out by gender only statsbomb templates
  const teamTemplateConfigs = filterRadarTemplates(radarTemplates, 'team', gender);

  return teamTemplateConfigs;
});

export const teamRadarTemplatesAtom = atom(async get => {
  const radarTemplateConfigs = await get(teamRadarTemplateConfigsAtom);
  const formattedTeamRadarTemplates = formatRadarTemplates(radarTemplateConfigs);

  return formattedTeamRadarTemplates;
});

export const unwrappedTeamRadarTemplatesAtom = unwrap(teamRadarTemplatesAtom, prev => prev || []);
